import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Copy, ExternalLink, Check, LogOut, Crown, MapPin, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { analytics } from "@/lib/analytics";
import { publishDisplayName } from "@/hooks/useDisplayName";
import { celebrate } from "@/lib/celebrate";
import LinkedInPanel from "@/components/LinkedInPanel";
import { COUNTRIES_GEO, countryByIso, guessCountryIso } from "@/data/geo";
import { ContentCountryCard } from "@/components/settings/ContentCountryCard";
import { lookupPostal, localPhoneDigits, toE164, needsPostal } from "@/lib/geo";
import { recommendBand } from "@/lib/ageBand";
import { missingCrmFields, syncProfileToCrm, type CrmProfile } from "@/lib/crm";


type Profile = {
  id: string;
  full_name: string | null;
  email: string | null;
  age: string | null;
  date_of_birth: string | null;
  grade: string | null;
  school: string | null;
  city: string | null;
  state_region: string | null;
  postal_code: string | null;
  country: string | null;
  country_code: string | null;
  phone: string | null;
  phone_country_code: string | null;
  phone_e164: string | null;
  guardian_name: string | null;
  guardian_email: string | null;
  guardian_phone: string | null;
  marketing_opt_in: boolean;
  whatsapp_opt_in: boolean;
  crm_provider: string | null;
  crm_contact_id: string | null;
  crm_synced_at: string | null;
  crm_sync_error: string | null;
  headline: string | null;
  avatar_url: string | null;
  referral_handle: string | null;
  locked_at: string | null;
  portfolio_public: boolean;
  portfolio_tagline: string | null;
};

type Payment = {
  id: string;
  created_at: string;
  payment_status: string | null;
  final_price: number | null;
  list_price: number | null;
  currency: string | null;
  payment_provider: string | null;
  razorpay_payment_id: string | null;
  paypal_capture_id: string | null;
};

type Membership = {
  id: string;
  plan: string | null;
  started_at: string | null;
  expires_at: string | null;
};

type EditableKey =
  | "full_name"
  | "date_of_birth"
  | "grade"
  | "school"
  | "country_code"
  | "postal_code"
  | "city"
  | "state_region"
  | "phone"
  | "guardian_name"
  | "guardian_email"
  | "guardian_phone"
  | "avatar_url"
  | "portfolio_tagline";

const EDITABLE_KEYS: EditableKey[] = [
  "full_name",
  "date_of_birth",
  "grade",
  "school",
  "country_code",
  "postal_code",
  "city",
  "state_region",
  "phone",
  "guardian_name",
  "guardian_email",
  "guardian_phone",
  "avatar_url",
  "portfolio_tagline",
];

type Draft = Record<EditableKey, string>;

function toDraft(profile: Profile): Draft {
  return EDITABLE_KEYS.reduce((acc, key) => {
    acc[key] = (profile[key] as string | null) ?? "";
    return acc;
  }, {} as Draft);
}

export default function Settings() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState<Profile | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [crmSyncing, setCrmSyncing] = useState(false);

  useEffect(() => {
    analytics.settingsView();
    if (!user) return;
    supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          const row = data as Profile;
          setP(row);
          const d = toDraft(row);
          // A sensible default, never a silent save — the person still confirms it.
          if (!d.country_code) d.country_code = guessCountryIso() ?? "";
          setDraft(d);
        }
        setLoading(false);
      });
    supabase
      .from("payments")
      .select("id, created_at, payment_status, final_price, list_price, currency, payment_provider, razorpay_payment_id, paypal_capture_id")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .then(({ data }) => setPayments((data as Payment[]) ?? []));
    supabase
      .from("memberships")
      .select("id, plan, started_at, expires_at")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => setMembership(data as Membership | null));
  }, [user]);

  async function save(patch: Partial<Profile>) {
    if (!user) return false;
    setSaving(true);
    const { data, error } = await supabase
      .from("profiles")
      .update(patch)
      .eq("id", user.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return false;
    }
    if (data) {
      setP(data as Profile);
      setDraft(toDraft(data as Profile));
    }
    return true;
  }

  const isLocked = !!p?.locked_at;

  const dirtyKeys: EditableKey[] = (p && draft)
    ? EDITABLE_KEYS.filter((k) => {
        if (isLocked && (k === "full_name" || k === "date_of_birth")) return false;
        return draft[k].trim() !== (((p[k] as string | null) ?? "").trim());
      })
    : [];
  const isDirty = dirtyKeys.length > 0;

  function set(key: EditableKey, value: string) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  const country = countryByIso(draft?.country_code);
  const recommendation = recommendBand(draft?.date_of_birth ?? null, p?.age ?? null);

  /** What the CRM still needs before a contact can be created. */
  const crmMissing = useMemo(() => (p ? missingCrmFields(p as unknown as CrmProfile) : []), [p]);

  /** Postal code -> city + region. Only fills blanks; never overwrites a typed value. */
  async function autofillFromPostal() {
    if (!draft?.country_code || !draft.postal_code.trim()) return;
    setLookingUp(true);
    const places = await lookupPostal(draft.country_code, draft.postal_code);
    setLookingUp(false);
    if (places.length === 0) {
      toast.info("We couldn't find that postal code — type your city instead.");
      return;
    }
    const first = places[0];
    setDraft((d) =>
      d
        ? {
            ...d,
            city: first.city || d.city,
            state_region: first.region || d.state_region,
          }
        : d,
    );
    toast.success(`Found ${first.city}${first.region ? `, ${first.region}` : ""}`);
  }

  async function saveAll() {
    if (!draft || !isDirty) return;
    const patch: Partial<Profile> = {};
    for (const k of dirtyKeys) {
      const v = draft[k].trim();
      (patch as Record<string, string | null>)[k] = v === "" ? null : v;
    }
    // Derived, never typed: country name, dialling code, and the E.164 phone.
    if (dirtyKeys.includes("country_code") || dirtyKeys.includes("phone")) {
      const c = countryByIso(draft.country_code);
      patch.country = c?.name ?? null;
      patch.phone_country_code = c?.dial ?? null;
      patch.phone_e164 = toE164(draft.phone, draft.country_code);
    }
    const ok = await save(patch);
    if (ok) {
      if (patch.full_name !== undefined) publishDisplayName(patch.full_name);
      celebrate("small");
      toast.success("Settings saved — taking you to your dashboard");
      window.setTimeout(() => navigate("/dashboard"), 700);
    }
  }

  function resetAll() {
    if (p) setDraft(toDraft(p));
  }

  async function runCrmSync() {
    if (!p) return;
    setCrmSyncing(true);
    const res = await syncProfileToCrm();
    setCrmSyncing(false);
    if (res.ok) {
      toast.success("Contact synced to your CRM");
      if (user) {
        const { data } = await supabase.from("profiles").select("*").eq("id", user.id).maybeSingle();
        if (data) setP(data as Profile);
      }
    } else {
      toast.error(res.message ?? "CRM sync failed");
    }
  }

  // Warn before losing unsaved edits
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const handle = p?.referral_handle ?? "";
  const portfolioUrl = handle ? `${window.location.origin}/p/${handle}` : "";

  function copyUrl() {
    if (!portfolioUrl) return;
    navigator.clipboard.writeText(portfolioUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (loading || !p || !draft) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>
    );
  }


  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Button asChild variant="ghost" size="sm" className="gap-1.5 -ml-2">
            <Link to="/dashboard"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
          </Button>
          <div className="flex items-center gap-2">
            {isDirty && <span className="text-xs text-muted-foreground hidden sm:inline">Unsaved changes</span>}
            <Button size="sm" className="rounded-full min-h-11 px-4" onClick={saveAll} disabled={!isDirty || saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </header>


      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">
            One place for who you are, where you are, and how we reach you.
          </p>
        </div>

        {/* Identity (locked after first assessment) */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-display text-xl font-semibold">Identity</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {isLocked
                    ? "Locked to keep your growth journey honest."
                    : "Locks automatically after your first assessment."}
                </p>
              </div>
              {isLocked && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                      <Badge variant="outline" className="gap-1.5"><Lock className="h-3 w-3" /> Locked</Badge>
                    </span>
                  </TooltipTrigger>

                  <TooltipContent>Contact support to change locked fields.</TooltipContent>
                </Tooltip>
              )}
            </div>

            <Field label="Full name" htmlFor="set-full-name">
              <Input
                id="set-full-name"
                value={draft.full_name}
                disabled={isLocked}
                autoComplete="name"
                onChange={(e) => set("full_name", e.target.value)}
              />
            </Field>
            <Field label="Date of birth" htmlFor="set-dob">
              <Input
                id="set-dob"
                type="date"
                value={draft.date_of_birth}
                disabled={isLocked}
                max={new Date().toISOString().slice(0, 10)}
                autoComplete="bday"
                onChange={(e) => set("date_of_birth", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                {recommendation.age != null
                  ? `You're ${recommendation.age}. That puts you in the ${recommendation.label} assessment.`
                  : "Your date of birth decides which assessment fits you."}
              </p>
            </Field>
            {recommendation.band && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/40 px-4 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
                  <span>Your assessment: <strong>{recommendation.label}</strong></span>
                </div>
                <Button asChild size="sm" variant="outline" className="rounded-full min-h-11">
                  <Link to={recommendation.href}>Open it</Link>
                </Button>
              </div>
            )}
            <Field label="Email" htmlFor="set-email">
              <Input id="set-email" value={p.email ?? ""} disabled autoComplete="email" />
            </Field>
          </CardContent>
        </Card>

        {/* Location & contact — the CRM-critical block */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" aria-hidden="true" /> Location & contact
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Pick your country and we'll set the dialling code and fill your city from your postal code.
              </p>
            </div>

            <Field label="Country" htmlFor="set-country">
              <Select
                value={draft.country_code || undefined}
                onValueChange={(v) => set("country_code", v)}
              >
                <SelectTrigger id="set-country" className="min-h-11">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES_GEO.map((c) => (
                    <SelectItem key={c.iso2} value={c.iso2}>
                      {c.flag} {c.name} ({c.dial})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {needsPostal(draft.country_code) && (
              <Field label="Postal / ZIP code" htmlFor="set-postal">
                <div className="flex gap-2">
                  <Input
                    id="set-postal"
                    value={draft.postal_code}
                    autoComplete="postal-code"
                    onChange={(e) => set("postal_code", e.target.value)}
                    onBlur={() => {
                      if (country?.postalLookup) void autofillFromPostal();
                    }}
                    placeholder={country?.postalLookup ? "We'll look this up" : "Postal code"}
                  />
                  {country?.postalLookup && (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full min-h-11 shrink-0"
                      onClick={() => void autofillFromPostal()}
                      disabled={lookingUp || !draft.postal_code.trim()}
                    >
                      {lookingUp ? "Looking…" : "Find city"}
                    </Button>
                  )}
                </div>
              </Field>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="City" htmlFor="set-city">
                <Input
                  id="set-city"
                  value={draft.city}
                  autoComplete="address-level2"
                  onChange={(e) => set("city", e.target.value)}
                />
              </Field>
              <Field label="State / region" htmlFor="set-region">
                <Input
                  id="set-region"
                  value={draft.state_region}
                  autoComplete="address-level1"
                  onChange={(e) => set("state_region", e.target.value)}
                />
              </Field>
            </div>

            <Field label="Phone" htmlFor="set-phone">
              <div className="flex gap-2">
                <span className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-input bg-muted/50 px-3 text-sm text-muted-foreground min-h-11 min-w-[4.5rem]">
                  {country?.dial ?? "+ __"}
                </span>
                <Input
                  id="set-phone"
                  value={draft.phone}
                  inputMode="tel"
                  autoComplete="tel-national"
                  onChange={(e) => set("phone", e.target.value)}
                  onBlur={() => set("phone", localPhoneDigits(draft.phone, country))}
                  placeholder="Local number, no country code"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {toE164(draft.phone, draft.country_code)
                  ? `Saved as ${toE164(draft.phone, draft.country_code)}`
                  : "Pick a country first so we can store an international number."}
              </p>
            </Field>
          </CardContent>
        </Card>

        <ContentCountryCard />

        {/* Study */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <h2 className="font-display text-xl font-semibold">Study & work</h2>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Grade / Year" htmlFor="set-grade">
                <Input
                  id="set-grade"
                  value={draft.grade}
                  onChange={(e) => set("grade", e.target.value)}
                  placeholder="e.g. Grade 11, Year 2 BSc"
                />
              </Field>
              <Field label="School / College" htmlFor="set-school">
                <Input
                  id="set-school"
                  value={draft.school}
                  autoComplete="organization"
                  onChange={(e) => set("school", e.target.value)}
                />
              </Field>
            </div>
            <Field label="Profile photo" htmlFor="set-avatar">
              <Input
                id="set-avatar"
                value={draft.avatar_url}
                onChange={(e) => set("avatar_url", e.target.value)}
                placeholder="Storage path or https:// URL"
              />
            </Field>
          </CardContent>
        </Card>

        {/* Guardian — only for minors */}
        {recommendation.needsGuardian && (
          <Card>
            <CardContent className="p-6 space-y-5">
              <div>
                <h2 className="font-display text-xl font-semibold">Parent or guardian</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  So we can send them your results and ask what they've noticed in you.
                </p>
              </div>
              <Field label="Their name" htmlFor="set-guardian-name">
                <Input
                  id="set-guardian-name"
                  value={draft.guardian_name}
                  onChange={(e) => set("guardian_name", e.target.value)}
                />
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Their email" htmlFor="set-guardian-email">
                  <Input
                    id="set-guardian-email"
                    type="email"
                    value={draft.guardian_email}
                    onChange={(e) => set("guardian_email", e.target.value)}
                  />
                </Field>
                <Field label="Their phone" htmlFor="set-guardian-phone">
                  <Input
                    id="set-guardian-phone"
                    inputMode="tel"
                    value={draft.guardian_phone}
                    onChange={(e) => set("guardian_phone", e.target.value)}
                  />
                </Field>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Consent + CRM */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <h2 className="font-display text-xl font-semibold">How we reach you</h2>
              <p className="text-xs text-muted-foreground mt-1">
                You can change this any time. We never sell your details.
              </p>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium">Email updates</div>
                <p className="text-xs text-muted-foreground mt-1">Guidance, new pathways and offers.</p>
              </div>
              <Switch
                aria-label="Receive email updates"
                checked={p.marketing_opt_in}
                disabled={saving}
                onCheckedChange={async (v) => {
                  const ok = await save({ marketing_opt_in: v });
                  if (ok) toast.success(v ? "Email updates on" : "Email updates off");
                }}
              />
            </div>

            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium">WhatsApp / SMS</div>
                <p className="text-xs text-muted-foreground mt-1">Nudges on your number above.</p>
              </div>
              <Switch
                aria-label="Receive WhatsApp or SMS messages"
                checked={p.whatsapp_opt_in}
                disabled={saving || !p.phone_e164}
                onCheckedChange={async (v) => {
                  const ok = await save({ whatsapp_opt_in: v });
                  if (ok) toast.success(v ? "WhatsApp nudges on" : "WhatsApp nudges off");
                }}
              />
            </div>

            <div className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">CRM record</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {p.crm_synced_at
                      ? `Synced to ${p.crm_provider === "hubspot" ? "HubSpot" : "Zoho CRM"} on ${new Date(p.crm_synced_at).toLocaleDateString()}.`
                      : "Not synced yet."}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-full min-h-11 shrink-0"
                  onClick={() => void runCrmSync()}
                  disabled={crmSyncing || crmMissing.length > 0 || isDirty}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${crmSyncing ? "animate-spin" : ""}`} aria-hidden="true" />
                  {crmSyncing ? "Syncing…" : "Sync now"}
                </Button>
              </div>
              {isDirty && (
                <p className="text-xs text-muted-foreground">Save your changes before syncing.</p>
              )}
              {crmMissing.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Still needed: {crmMissing.join(", ")}.
                </p>
              )}
              {p.crm_sync_error && (
                <p className="text-xs text-destructive">Last sync failed. We'll retry on your next save.</p>
              )}
            </div>
          </CardContent>
        </Card>



        {/* LinkedIn */}
        <LinkedInPanel
          onProfileImported={() => {
            if (!user) return;
            supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => {
              if (data) {
                setP(data as Profile);
                setDraft(toDraft(data as Profile));
              }
            });
          }}
        />

        {/* Portfolio */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold">Public portfolio</h2>
                <p className="text-xs text-muted-foreground mt-1 max-w-md">
                  Share your growth journey with mentors, recruiters or family.
                </p>
              </div>
              <Switch
                aria-label="Make my portfolio public"
                checked={p.portfolio_public}
                onCheckedChange={async (v) => {
                  const ok = await save({ portfolio_public: v });
                  if (ok) toast.success(v ? "Portfolio is now public" : "Portfolio is now private");
                }}
                disabled={saving}
              />
            </div>


            <Field label="Tagline" htmlFor="set-tagline">
              <Textarea
                id="set-tagline"
                value={draft.portfolio_tagline}
                onChange={(e) => set("portfolio_tagline", e.target.value)}
                placeholder="A one-liner about who you're becoming"
                className="min-h-20"
              />
            </Field>


            {handle ? (
              <div className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/40 px-3 py-2">
                <code className="text-xs sm:text-sm flex-1 truncate">{portfolioUrl}</code>
                <Button size="sm" variant="ghost" className="min-h-11 min-w-11" aria-label="Copy portfolio link" onClick={copyUrl}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
                <Button size="sm" variant="ghost" className="min-h-11 min-w-11" asChild>
                  <a href={portfolioUrl} target="_blank" rel="noreferrer" aria-label="Open portfolio in a new tab"><ExternalLink className="h-4 w-4" /></a>
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Your portfolio handle will be generated after your first reflection or referral action.</p>
            )}
          </CardContent>
        </Card>

        {/* Subscription & Payments */}
        <Card>
          <CardContent className="p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-semibold flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" /> Subscription
                </h2>
                <p className="text-xs text-muted-foreground mt-1">Your active plan and renewal.</p>
              </div>
              {membership && membership.expires_at && new Date(membership.expires_at) > new Date() ? (
                <Badge className="bg-primary/10 text-primary border-primary/20" variant="outline">Active</Badge>
              ) : (
                <Badge variant="outline">Inactive</Badge>
              )}
            </div>

            {membership ? (
              <div className="grid grid-cols-2 gap-4 rounded-lg border border-border/60 bg-muted/30 p-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Plan</div>
                  <div className="text-sm font-medium mt-1 capitalize">{membership.plan ?? "—"}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Renews / Expires</div>
                  <div className="text-sm font-medium mt-1">
                    {membership.expires_at ? new Date(membership.expires_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Started</div>
                  <div className="text-sm font-medium mt-1">
                    {membership.started_at ? new Date(membership.started_at).toLocaleDateString() : "—"}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/60 p-4 flex items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">No active subscription yet.</p>
                <Button size="sm" onClick={() => navigate("/payment")}>Upgrade</Button>
              </div>
            )}

            <div>
              <h3 className="font-display text-base font-semibold mb-3">Payment history</h3>
              {payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No payments yet.</p>
              ) : (
                <div className="divide-y divide-border/60 rounded-lg border border-border/60 overflow-hidden">
                  {payments.map((pay) => {
                    const amt = pay.final_price ?? pay.list_price ?? 0;
                    const cur = (pay.currency ?? "INR").toUpperCase();
                    const symbol = cur === "USD" ? "$" : cur === "INR" ? "₹" : `${cur} `;
                    
                    const status = (pay.payment_status ?? "pending").toLowerCase();
                    const statusColor =
                      status === "success"
                        ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                        : status === "failed"
                          ? "bg-destructive/10 text-destructive border-destructive/20"
                          : "bg-muted text-muted-foreground border-border";
                    return (
                      <div key={pay.id} className="flex items-center justify-between gap-3 px-4 py-3 bg-background">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {symbol}
                            {amt.toLocaleString()} {cur !== "INR" && cur !== "USD" ? "" : ""}
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            {new Date(pay.created_at).toLocaleDateString()} ·{" "}
                            <span className="capitalize">{pay.payment_provider ?? "—"}</span>
                          </div>
                        </div>
                        <Badge variant="outline" className={`capitalize ${statusColor}`}>{status}</Badge>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </CardContent>
        </Card>



        <Card className="rounded-2xl border border-border/60">
          <CardContent className="p-5 sm:p-6 flex items-center justify-between gap-4">
            <div>
              <div className="font-display text-base font-semibold text-foreground">Sign out</div>
              <p className="text-xs text-muted-foreground mt-1">End your session on this device.</p>
            </div>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={async () => {
                await signOut();
                toast.success("Signed out");
                navigate("/", { replace: true });
              }}
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </CardContent>
        </Card>

        {isDirty && (
          <div
            role="status"
            aria-live="polite"
            className="sticky bottom-4 z-30 flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-background/95 px-4 py-3 shadow-lg backdrop-blur"
          >
            <p className="text-sm text-muted-foreground">
              You have {dirtyKeys.length} unsaved change{dirtyKeys.length === 1 ? "" : "s"}.
            </p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="min-h-11 rounded-full" onClick={resetAll} disabled={saving}>
                Discard
              </Button>
              <Button size="sm" className="min-h-11 rounded-full px-6" onClick={saveAll} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        )}
      </main>

    </div>
  );
}

function Field({ label, htmlFor, children }: { label: string; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</Label>
      {children}
    </div>
  );
}
